import {
  HighFiveMini,
  LockMini,
  MailboxMini,
} from "@dropbox/dig-illustrations";
import { redirectModalVisibleOrLoadingAtom } from "@mirage/eligibility/redirectModalAtoms";
import type { RedirectModalProps } from "@mirage/growth/components/RedirectModal/RedirectModal";
import { RedirectModal } from "@mirage/growth/components/RedirectModal/RedirectModal";
import useDropboxAccount, {
  isAdminUser,
} from "@mirage/service-auth/useDropboxAccount";
import { callApiV2 } from "@mirage/service-dbx-api";
import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { tagged } from "@mirage/service-logging";
import { openURL } from "@mirage/service-platform-actions";
import {
  DASH_PLANS,
  DASH_WEBAPP_DOMAIN,
  DROPBOX_HOME_URL,
  DROPBOX_MANAGE_SUBSCRIPTIONS_URL,
  DROPBOX_ONBOARDING_START_URL,
  DROPBOX_TEAM_JOIN_URL,
} from "@mirage/shared/urls";
import i18n, { Trans } from "@mirage/translations";
import { useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useWebOnboardingEligibility } from "./hooks/useWebOnboardingEligibility";
import { RedirectVariant } from "./types";

const logger = tagged("FullPageRedirect");

interface Props {
  variant: RedirectVariant;
  isDfbUser?: boolean;
  isEligible?: boolean;
  isDevboxBackend?: boolean;
}

export const FullPageRedirect = ({
  variant,
  isEligible = false,
  isDevboxBackend = false,
}: Props) => {
  const setModalVisibleOrLoading = useSetAtom(
    redirectModalVisibleOrLoadingAtom,
  );
  const { shouldShowRedirectModal } = useWebOnboardingEligibility();
  const [dismissed, setDismissed] = useState(false);
  const [shouldShowModal, setShouldShowModal] = useState<boolean>(false);
  const currentAccount = useDropboxAccount();
  const isAdmin = isAdminUser(currentAccount);
  const location = useLocation();
  const isDesktop = EnvCtx.surface == "desktop";

  const [verifyEmailSent, setVerifyEmailSent] = useState(false);

  const teamName = currentAccount?.team?.name ?? i18n.t("fallback_team_name");

  const modalConfig = {
    [RedirectVariant.CountryBlock]: {
      asset: <LockMini altText={i18n.t("lock")} />,
      description: i18n.t("blocked_country_description"),
      title: i18n.t("blocked_country_title"),
    },
    [RedirectVariant.GenericBlock]: {
      asset: <LockMini altText={i18n.t("lock")} />,
      description: i18n.t("blocked_generic_description"),
      title: i18n.t("blocked_generic_title"),
    },
    [RedirectVariant.PendingInviteBlock]: {
      asset: <MailboxMini altText={i18n.t("mailbox")} />,
      description: i18n.t("blocked_pending_invite_description", {
        teamName,
      }),
      title: i18n.t("blocked_pending_invite_title"),
      primaryCta: {
        label: i18n.t("join_team_cta"),
        onClick: () => {
          openURL(DROPBOX_TEAM_JOIN_URL);
        },
      },
    },
    [RedirectVariant.DfbOnboarding]: {
      asset: <HighFiveMini altText={i18n.t("high_five")} />,
      description: i18n.t("blocked_onboarding_description"),
      title: i18n.t("blocked_onboarding_title"),
      primaryCta: {
        label: i18n.t("blocked_onboarding_cta"),
        onClick: () => {
          setDismissed(true);
          setModalVisibleOrLoading(false);
          const onboardingUrl = isDevboxBackend
            ? // bypassing devbox url that redirects user to correct onboarding page and go to DfB onboarding start url
              window.location.origin + "/setup/team"
            : DROPBOX_ONBOARDING_START_URL;
          openURL(onboardingUrl);
        },
      },
    },
    [RedirectVariant.UnverifiedEmail]: {
      asset: <MailboxMini altText={i18n.t("mailbox")} />,
      description: (
        <Trans
          i18nKey={"blocked_unverified_email_description"}
          values={{
            email: currentAccount?.email,
          }}
          components={{
            bold: <b></b>,
          }}
        />
      ),
      title: i18n.t("blocked_unverified_email_title"),
      secondaryCta: {
        label: verifyEmailSent
          ? i18n.t("blocked_unverified_email_resend_button_sent")
          : i18n.t("blocked_unverified_email_resend_button"),
        disabled: verifyEmailSent,
        onClick: () => {
          setVerifyEmailSent(true);
          callApiV2("securitySettingsSendVerifyEmailV2", {
            verification_reason: { ".tag": "dash_unverified_email" },
            type: { ".tag": "link" },
          });
        },
      },
    },
    [RedirectVariant.NoLicenseBlock]: {
      asset: <LockMini altText={i18n.t("lock")} />,
      description: i18n.t("blocked_user_no_license_description"),
      title: i18n.t("blocked_user_no_license_title"),
    },
    [RedirectVariant.BridgeOnboarding]: isDesktop
      ? {
          asset: <HighFiveMini altText={i18n.t("high_five")} />,
          description: i18n.t("blocked_onboarding_description"),
          title: i18n.t("blocked_onboarding_title"),
          primaryCta: {
            label: i18n.t("blocked_onboarding_cta"),
            onClick: () => {
              setDismissed(true);
              setModalVisibleOrLoading(false);
              openURL(DASH_WEBAPP_DOMAIN);
            },
          },
        }
      : null,
    [RedirectVariant.SuspendedPaidSubscription]: {
      asset: <LockMini altText={i18n.t("lock")} />,
      title: i18n.t("suspended_paid_subscription_title", {
        teamName,
      }),
      description: isAdmin
        ? i18n.t("suspended_paid_subscription_description_admin")
        : i18n.t("suspended_paid_subscription_description_member"),
      primaryCta: isAdmin
        ? {
            label: i18n.t("suspended_paid_subscription_cta"),
            onClick: () => {
              openURL(DROPBOX_MANAGE_SUBSCRIPTIONS_URL);
            },
          }
        : undefined,
    },
    [RedirectVariant.SuspendedTrialSubscription]: {
      // TODO: replace with correct asset from content design when available
      asset: <LockMini altText={i18n.t("lock")} />,
      title: i18n.t("suspended_trial_subscription_title", {
        teamName,
      }),
      description: i18n.t("suspended_trial_subscription_description"),
      primaryCta: isAdmin
        ? {
            label: i18n.t("suspended_trial_subscription_primary_cta"),
            onClick: () => {
              openURL(DASH_PLANS);
            },
          }
        : undefined,
      secondaryCta: {
        label: i18n.t("suspended_trial_subscription_secondary_cta"),
        onClick: () => {
          openURL(DROPBOX_HOME_URL);
        },
      },
    },
  };

  const getConfigForVariant = (
    variant: RedirectVariant,
  ): (RedirectModalProps & { variant: RedirectVariant }) | undefined => {
    const config = modalConfig[variant];

    if (!config && variant && !isEligible) {
      logger.error(
        `Ineligibile user navigated to invalid redirect page variant: ${variant}`,
      );
      const defaultConfig = modalConfig[RedirectVariant.GenericBlock];
      return { ...defaultConfig, variant };
    }

    if (!config && variant && isEligible) {
      return;
    }

    return { ...config, variant };
  };

  const props = getConfigForVariant(variant);
  useEffect(() => {
    const checkShouldShowModal = async () => {
      const shouldShow = await shouldShowRedirectModal({
        variant,
        pathname: location.pathname,
        protocol: window.location.protocol,
      });
      setShouldShowModal(shouldShow);
      setModalVisibleOrLoading(shouldShow);
    };
    checkShouldShowModal();
  }, [
    variant,
    location.pathname,
    shouldShowRedirectModal,
    setModalVisibleOrLoading,
  ]);

  if (dismissed || !props || !shouldShowModal) {
    return null;
  }

  return <RedirectModal {...props} />;
};
